const toOptions = (values) => values.map((value) => ({ value, label: value }));

/** [code, label] pairs → { value: "CODE:label", label: "CODE: label" } */
const toCodedOptions = (entries) =>
  entries.map(([code, label]) => ({
    value: `${code}:${label}`,
    label: `${code}: ${label}`,
  }));

/** Default patient types used by most workflow specialties. */
export const DEFAULT_PATIENT_TYPE_OPTIONS = toOptions([
  "NEW",
  "FOLLOWUP",
  "TELEMED",
  "TELEMED_NEW",
  "TELEMED_FOLLOWUP",
  "ONCOLOGY_VISIT",
  "POST_OP",
  "REMOTE",
  "ASSESSMENT",
  "TCM",
]);

/**
 * Workflow-specific patient type lists keyed by normalized specialty name.
 * Add new entries here when another specialty needs its own checkbox set.
 */
export const WORKFLOW_PATIENT_TYPE_OPTIONS_BY_SPECIALTY = {
  boomerang: toCodedOptions([
    ["992", "WC PTP New Patient"],
    ["992", "PTP New Patient"],
    ["A4", "WC Secondary New Patient"],
    ["991", "WC PTP Follow Up"],
    ["990", "WC PTP Extended Follow Up"],
    ["A17", "WC Telemed PTP Follow Up"],
    ["A5", "WC Secondary Follow Up"],
    ["A6", "WC Secondary Extended Follow Up"],
    ["A9", "WC Telemed STP Follow Up"],
    ["A36", "Telemed Status Recheck"],
    ["A35", "Status Recheck"],
    ["970", "PI New Patient"],
    ["A14", "PI Telemed New Patient"],
    ["585", "PI Follow Up"],
    ["986", "PI Extended Follow Up"],
    ["A18", "PI Final"],
    ["418", "New Patient"],
    ["807", "Telemedicine New Patient"],
    ["484", "Follow Up"],
    ["985", "Extended Follow Up"],
    ["987", "PI In Office Injection"],
    ["147", "In Office Injection"],
    ["560", "Pre-Op"],
    ["559", "Post-Op"],
    ["143", "P&S (DO NOT SEND, SEE DICTATED REPORT)"],
    ["A65", "Psych Dept Psychology Intake"],
    ["A66", "Psych Dept Psychology Follow Up"],
    ["A75", "Psych Dept SCS/Pre-Surgical Eval"],
    ["A67", "WC Psych Dept PTP Intake"],
    ["A69", "WC Psych Dept PTP Follow Up"],
    ["A68", "WC Psych Dept Secondary Intake"],
    ["A70", "WC Psych Dept Secondary Follow up"],
  ]),
};

const normalizeSpecialty = (specialty) => specialty?.trim().toLowerCase() ?? "";

export const getPatientTypeOptions = (specialty) => {
  const key = normalizeSpecialty(specialty);
  return WORKFLOW_PATIENT_TYPE_OPTIONS_BY_SPECIALTY[key] ?? DEFAULT_PATIENT_TYPE_OPTIONS;
};

/**
 * Options for the route editor, including any values already saved on the route
 * that are not in the specialty's current list (e.g. legacy NEW/TELEMED on Boomerang).
 */
export const getPatientTypeOptionsForRoute = (specialty, selectedTypes = []) => {
  const baseOptions = getPatientTypeOptions(specialty);
  const knownValues = new Set(baseOptions.map((entry) => entry.value));
  const legacyOptions = (selectedTypes || [])
    .filter((value) => value && !knownValues.has(value))
    .map((value) => ({ value, label: value }));

  return [...baseOptions, ...legacyOptions];
};

export const getPatientTypeLabel = (value, specialty) => {
  const option = getPatientTypeOptions(specialty).find((entry) => entry.value === value);
  if (option) return option.label;

  const defaultOption = DEFAULT_PATIENT_TYPE_OPTIONS.find((entry) => entry.value === value);
  return defaultOption?.label ?? value;
};

/** Long custom lists get a scroll container in the route editor. */
export const shouldScrollPatientTypeList = (specialty) =>
  getPatientTypeOptions(specialty).length > DEFAULT_PATIENT_TYPE_OPTIONS.length;
